import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  CircularProgress,
  Alert,
  Divider,
  Stack,
  Card,
  CardContent,
  Tabs,
  Tab,
  Badge
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Build as BuildIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import { Vehicle, MaintenanceRecord, MaintenanceFormData } from '../../types';
import { maintenanceService, maintenanceSettingsService } from '../../services/api';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface MaintenanceManagementProps {
  vehicle: Vehicle;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`maintenance-tabpanel-${index}`}
      aria-labelledby={`maintenance-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const MaintenanceManagement: React.FC<MaintenanceManagementProps> = ({
  vehicle,
  open,
  onClose,
  onSuccess
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [maintenanceRecords, setMaintenanceRecords] = useState<MaintenanceRecord[]>([]);
  const [maintenanceTypes, setMaintenanceTypes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form states
  const [showForm, setShowForm] = useState(false);
  const [editingRecord, setEditingRecord] = useState<MaintenanceRecord | null>(null);
  const [formData, setFormData] = useState<MaintenanceFormData>({
    vehicle_id: vehicle.id,
    maintenance_type: '',
    description: '',
    start_date: format(new Date(), 'yyyy-MM-dd'),
  });

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open, vehicle.id]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [maintenanceRes, typesRes] = await Promise.all([
        maintenanceService.getByVehicle(vehicle.id),
        maintenanceSettingsService.getSettings()
      ]);
      
      setMaintenanceRecords(maintenanceRes.data.data || []);
      
      // Process maintenance types (now only client-specific)
      const settings = typesRes.data.data;
      setMaintenanceTypes(settings.types || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Ensure description has a default value if empty
      const submitData = {
        ...formData,
        description: formData.description || `Maintenance ${formData.maintenance_type}`
      };

      if (editingRecord) {
        // Only allow editing manual records (numeric IDs)
        if (typeof editingRecord.id === 'string') {
          setError('Les enregistrements calculés ne peuvent pas être modifiés');
          return;
        }
        await maintenanceService.update(editingRecord.id, submitData);
        setSuccess('Maintenance modifiée avec succès!');
      } else {
        await maintenanceService.create(submitData);
        setSuccess('Maintenance créée avec succès!');
      }
      
      // Clear form and reset state
      setFormData({
        vehicle_id: vehicle.id,
        maintenance_type: '',
        description: '',
        start_date: format(new Date(), 'yyyy-MM-dd'),
      });
      setShowForm(false);
      setEditingRecord(null);
      
      // Reload data to show the new/updated record
      await loadData();
      
      // Auto-hide success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (record: MaintenanceRecord) => {
    setEditingRecord(record);
    setFormData({
      vehicle_id: vehicle.id,
      maintenance_type: record.maintenance_type,
      description: record.description || '',
      start_date: record.start_date ? format(new Date(record.start_date), 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
      completion_date: record.completion_date ? format(new Date(record.completion_date), 'yyyy-MM-dd') : '',
      cost: record.cost || undefined,
      service_provider: record.service_provider || '',
      notes: record.notes || '',
    });
    setShowForm(true);
    setTabValue(2); // Switch to the form tab
    setError('');
    setSuccess('');
  };

  const handleMarkCompleted = async (record: MaintenanceRecord) => {
    if (typeof record.id === 'string') {
      setError('Les enregistrements calculés ne peuvent pas être modifiés');
      return;
    }

    setLoading(true);
    try {
      await maintenanceService.update(record.id, {
        vehicle_id: record.vehicle_id,
        maintenance_type: record.maintenance_type,
        description: record.description,
        cost: record.cost,
        service_provider: record.service_provider,
        start_date: record.start_date,
        completion_date: format(new Date(), 'yyyy-MM-dd'),
        technician_id: record.technician_id,
        notes: record.notes
      });
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingRecord(null);
    setFormData({
      vehicle_id: vehicle.id,
      maintenance_type: '',
      description: '',
      start_date: format(new Date(), 'yyyy-MM-dd'),
    });
    setError('');
    setSuccess('');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'warning';
      case 'scheduled': return 'info';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed': return 'Terminé';
      case 'in_progress': return 'En cours';
      case 'scheduled': return 'Programmé';
      case 'cancelled': return 'Annulé';
      default: return status;
    }
  };

  const upcomingMaintenance = maintenanceRecords.filter(record => 
    record.status === 'scheduled' || record.status === 'in_progress'
  );

  const completedMaintenance = maintenanceRecords.filter(record => 
    record.status === 'completed'
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            <BuildIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Maintenance - {vehicle.registration_number}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ m: 2 }}>
            {success}
          </Alert>
        )}

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
            <Tab 
              label={
                <Badge badgeContent={upcomingMaintenance.length} color="primary">
                  À faire
                </Badge>
              } 
            />
            <Tab 
              label={
                <Badge badgeContent={completedMaintenance.length} color="success">
                  Terminé
                </Badge>
              } 
            />
            <Tab label="Nouvelle maintenance" />
          </Tabs>
        </Box>

        {/* Upcoming Maintenance Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingRecord(null);
                setFormData({
                  vehicle_id: vehicle.id,
                  maintenance_type: '',
                  description: '',
                  start_date: format(new Date(), 'yyyy-MM-dd'),
                });
                setShowForm(true);
                setTabValue(2);
                setError('');
                setSuccess('');
              }}
              sx={{ mb: 2 }}
            >
              Nouvelle maintenance
            </Button>
          </Box>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : upcomingMaintenance.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <ScheduleIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Aucune maintenance programmée
              </Typography>
            </Box>
          ) : (
            <List>
              {upcomingMaintenance.map((record, index) => (
                <React.Fragment key={record.id}>
                  {index > 0 && <Divider />}
                  <ListItem sx={{ py: 2 }}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {record.maintenance_type || 'Maintenance'}
                          </Typography>
                          <Chip 
                            label={getStatusLabel(record.status)}
                            size="small"
                            color={getStatusColor(record.status) as any}
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {record.description || 'Aucune description'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {record.start_date && format(new Date(record.start_date), 'dd MMMM yyyy', { locale: fr })}
                          </Typography>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Stack direction="row" spacing={1}>
                        <IconButton 
                          size="small" 
                          onClick={() => handleEdit(record)}
                          disabled={typeof record.id === 'string'}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={() => handleMarkCompleted(record)}
                          disabled={typeof record.id === 'string'}
                          color="success"
                        >
                          <CheckCircleIcon />
                        </IconButton>
                      </Stack>
                    </ListItemSecondaryAction>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </TabPanel>

        {/* Completed Maintenance Tab */}
        <TabPanel value={tabValue} index={1}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : completedMaintenance.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Aucune maintenance terminée
              </Typography>
            </Box>
          ) : (
            <List>
              {completedMaintenance.map((record, index) => (
                <React.Fragment key={record.id}>
                  {index > 0 && <Divider />}
                  <ListItem sx={{ py: 2 }}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {record.maintenance_type || 'Maintenance'}
                          </Typography>
                          <Chip 
                            label="Terminé"
                            size="small"
                            color="success"
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {record.description || 'Aucune description'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Terminé le {record.completion_date && format(new Date(record.completion_date), 'dd MMMM yyyy', { locale: fr })}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </TabPanel>

        {/* New Maintenance Tab */}
        <TabPanel value={tabValue} index={2}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {editingRecord ? 'Modifier la maintenance' : 'Nouvelle maintenance'}
              </Typography>
              
              <Stack spacing={3}>
                <FormControl fullWidth>
                  <InputLabel>Type de maintenance</InputLabel>
                  <Select
                    value={formData.maintenance_type}
                    onChange={(e) => {
                      setFormData({ ...formData, maintenance_type: e.target.value });
                      
                      // Auto-calculate completion date based on maintenance type duration
                      if (e.target.value && formData.start_date) {
                        const selectedType = maintenanceTypes.find(type => type.name === e.target.value);
                        if (selectedType && selectedType.duration_days) {
                          const startDate = new Date(formData.start_date);
                          const completionDate = new Date(startDate);
                          completionDate.setDate(completionDate.getDate() + selectedType.duration_days);
                          setFormData(prev => ({ 
                            ...prev, 
                            maintenance_type: e.target.value,
                            completion_date: completionDate.toISOString().split('T')[0]
                          }));
                        }
                      }
                    }}
                    label="Type de maintenance"
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                          zIndex: 9999,
                        },
                      },
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                      },
                    }}
                  >
                    {maintenanceTypes.map(type => (
                      <MenuItem key={type.id} value={type.name}>
                        {type.name} {type.duration_days && `(${type.duration_days} jours)`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Décrivez la maintenance à effectuer..."
                />

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Date de début"
                    value={formData.start_date}
                    onChange={(e) => {
                      setFormData({ ...formData, start_date: e.target.value });
                      
                      // Auto-calculate completion date if maintenance type is selected
                      if (e.target.value && formData.maintenance_type) {
                        const selectedType = maintenanceTypes.find(type => type.name === formData.maintenance_type);
                        if (selectedType && selectedType.duration_days) {
                          const startDate = new Date(e.target.value);
                          const completionDate = new Date(startDate);
                          completionDate.setDate(completionDate.getDate() + selectedType.duration_days);
                          setFormData(prev => ({ 
                            ...prev, 
                            start_date: e.target.value,
                            completion_date: completionDate.toISOString().split('T')[0]
                          }));
                        }
                      }
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    fullWidth
                    type="date"
                    label="Date de fin (calculée automatiquement)"
                    value={formData.completion_date || ''}
                    onChange={(e) => setFormData({ ...formData, completion_date: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    helperText="Calculée automatiquement selon le type de maintenance"
                  />
                </Stack>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    fullWidth
                    label="Coût (€)"
                    type="number"
                    value={formData.cost || ''}
                    onChange={(e) => setFormData({ ...formData, cost: Number(e.target.value) })}
                  />
                  <TextField
                    fullWidth
                    label="Prestataire"
                    value={formData.service_provider || ''}
                    onChange={(e) => setFormData({ ...formData, service_provider: e.target.value })}
                  />
                </Stack>

                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={2}
                  value={formData.notes || ''}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Notes additionnelles..."
                />
              </Stack>
            </CardContent>
          </Card>
        </TabPanel>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        {tabValue === 2 && (
          <>
            <Button
              variant="outlined"
              onClick={handleCloseForm}
              disabled={loading}
            >
              Annuler
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
            >
              {editingRecord ? 'Modifier' : 'Créer'}
            </Button>
          </>
        )}
        <Button onClick={onClose}>
          Fermer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MaintenanceManagement;
